import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { programKeys } from './usePrograms';
import type { LearningFlow } from '@lms/shared/types';

export const flowKeys = {
  all: ['flows'] as const,
  lists: () => [...flowKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) => [...flowKeys.all, 'list', params] as const,
  details: () => [...flowKeys.all, 'detail'] as const,
  detail: (id: string) => [...flowKeys.details(), id] as const,
};

export function useFlows(params: { programId: string; levelId?: string }) {
  return useQuery({
    queryKey: flowKeys.list(params as Record<string, unknown>),
    queryFn: () => apiClient.learningFlows.list(params.programId, params.levelId),
    enabled: !!params.programId,
  });
}

export function useCreateFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<LearningFlow>) => apiClient.learningFlows.create(data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: programKeys.detail(variables.programId || ''),
      });
      void queryClient.invalidateQueries({ queryKey: flowKeys.lists() });
    },
  });
}

export function useUpdateFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<LearningFlow> }) =>
      apiClient.learningFlows.update(id, data),
    onSuccess: (_, { data }) => {
      if (data.programId) {
        void queryClient.invalidateQueries({ queryKey: programKeys.detail(data.programId) });
      }
      void queryClient.invalidateQueries({ queryKey: flowKeys.lists() });
    },
  });
}

export function useDeleteFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; programId: string }) => apiClient.learningFlows.delete(id),
    onSuccess: (_, { programId }) => {
      void queryClient.invalidateQueries({ queryKey: programKeys.detail(programId) });
      void queryClient.invalidateQueries({ queryKey: flowKeys.lists() });
    },
  });
}
